// =============================================================================
// SubmissionHistory.jsx
//
// Drawer that shows the user's past weekly submissions.
// - Loads list of weeks with submissions
// - Clicking a week shows the submission in read-only
// - "Copy to Current Week" button copies data into the active form
// =============================================================================

import React, { useState, useEffect } from 'react';
import {
    Drawer, Box, Typography, Stack, IconButton, List, ListItemButton,
    ListItemText, Divider, Chip, Button, CircularProgress, Alert,
    Paper, Grid,
} from '@mui/material';
import {
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    ArrowBack as BackIcon,
    History as HistoryIcon,
    FlashOn as PresentIcon,
    AutoGraph as NextIcon,
    ReportProblem as IssueIcon,
    Assignment as AobIcon,
} from '@mui/icons-material';

import { apiGetMySubmissionHistory, apiGetMySubmission } from '../requests/weeklyUpdateRequests';


const SubmissionHistory = ({ open, onClose, currentWeekNumber, currentYear, onCopyToCurrentWeek }) => {

    const [history, setHistory]           = useState([]);
    const [loadingList, setLoadingList]   = useState(false);
    const [selectedWeek, setSelectedWeek] = useState(null);  // { weekNumber, year }
    const [preview, setPreview]           = useState(null);   // full submission data
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [error, setError]               = useState(null);

    // ── Load history list when drawer opens ──────────────────────────────────
    useEffect(() => {
        if (open) {
            setLoadingList(true);
            setError(null);
            setSelectedWeek(null);
            setPreview(null);

            apiGetMySubmissionHistory()
                .then((res) => setHistory(res.data ?? []))
                .catch(() => setError('Failed to load history.'))
                .finally(() => setLoadingList(false));
        }
    }, [open]);

    // ── Load preview when a week is selected ─────────────────────────────────
    const handleSelectWeek = (weekNumber, year) => {
        setSelectedWeek({ weekNumber, year });
        setLoadingPreview(true);
        setPreview(null);

        apiGetMySubmission(weekNumber, year)
            .then((res) => setPreview(res.data))
            .catch(() => setError('Failed to load submission.'))
            .finally(() => setLoadingPreview(false));
    };

    // ── Copy handler ─────────────────────────────────────────────────────────
    const handleCopy = () => {
        if (preview && onCopyToCurrentWeek) {
            onCopyToCurrentWeek({
                presentWeekActivities: preview.presentWeekActivities || [],
                nextWeekActivities:    preview.nextWeekActivities || [],
                issues:                preview.issues || [],
                aob:                   preview.aob || [],
            });
            onClose();
        }
    };

    // ── Check if a week is the current week ──────────────────────────────────
    const isCurrentWeek = (w) => w.weekNumber === currentWeekNumber && w.year === currentYear;

    // ── Filter out current week from history (no point copying from self) ────
    const pastWeeks = history.filter((w) => !isCurrentWeek(w));

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 0 } }}
        >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafbfc' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        {selectedWeek && (
                            <IconButton size="small" onClick={() => { setSelectedWeek(null); setPreview(null); }}>
                                <BackIcon fontSize="small" />
                            </IconButton>
                        )}
                        <HistoryIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                        <Typography variant="subtitle1" fontWeight={700}>
                            {selectedWeek
                                ? `Week ${selectedWeek.weekNumber}, ${selectedWeek.year}`
                                : 'Submission History'}
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>

            <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* ── LIST VIEW ───────────────────────────────────────────── */}
                {!selectedWeek && (
                    <>
                        {loadingList && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {!loadingList && pastWeeks.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                No previous submissions found.
                            </Typography>
                        )}

                        {!loadingList && pastWeeks.length > 0 && (
                            <List disablePadding>
                                {pastWeeks.map((w, i) => (
                                    <React.Fragment key={`${w.year}-${w.weekNumber}`}>
                                        <ListItemButton
                                            onClick={() => handleSelectWeek(w.weekNumber, w.year)}
                                            sx={{ borderRadius: 1.5, mb: 0.5 }}
                                        >
                                            <ListItemText
                                                primary={`Week ${w.weekNumber}, ${w.year}`}
                                                secondary={w.updatedAt ? `Last updated: ${new Date(w.updatedAt).toLocaleDateString('en-GB')}` : null}
                                                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                                                secondaryTypographyProps={{ fontSize: 12 }}
                                            />
                                            <Chip label="View" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                                        </ListItemButton>
                                        {i < pastWeeks.length - 1 && <Divider component="li" />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </>
                )}

                {/* ── PREVIEW VIEW ────────────────────────────────────────── */}
                {selectedWeek && (
                    <>
                        {loadingPreview && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {!loadingPreview && !preview && (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                No data found for this week.
                            </Typography>
                        )}

                        {!loadingPreview && preview && (
                            <Stack spacing={2.5}>

                                {/* Present Week Activities */}
                                <PreviewSection
                                    title="Present Week"
                                    icon={<PresentIcon fontSize="small" color="primary" />}
                                    items={preview.presentWeekActivities}
                                    renderItem={(a) => (
                                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f8f9fa', border: '1px solid #eee' }}>
                                            <Typography variant="caption" fontWeight={800} color="text.secondary">
                                                {a.technology} • {a.vendor}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>{a.description}</Typography>
                                        </Box>
                                    )}
                                />

                                {/* Next Week Activities */}
                                <PreviewSection
                                    title="Next Week"
                                    icon={<NextIcon fontSize="small" color="secondary" />}
                                    items={preview.nextWeekActivities}
                                    renderItem={(a) => (
                                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f8f9fa', border: '1px solid #eee' }}>
                                            <Typography variant="caption" fontWeight={800} color="text.secondary">
                                                {a.technology} • {a.vendor}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>{a.description}</Typography>
                                        </Box>
                                    )}
                                />

                                {/* Issues */}
                                <PreviewSection
                                    title="Issues & Risks"
                                    icon={<IssueIcon fontSize="small" color="error" />}
                                    items={preview.issues}
                                    renderItem={(item) => (
                                        <Typography variant="body2" sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'error.main' }}>
                                            {item.description}
                                        </Typography>
                                    )}
                                />

                                {/* AOB */}
                                <PreviewSection
                                    title="Other Business"
                                    icon={<AobIcon fontSize="small" color="action" />}
                                    items={preview.aob}
                                    renderItem={(item) => (
                                        <Typography variant="body2">• {item.description}</Typography>
                                    )}
                                />

                                {/* Copy button */}
                                <Button
                                    variant="contained"
                                    startIcon={<CopyIcon />}
                                    onClick={handleCopy}
                                    fullWidth
                                    sx={{
                                        mt: 2, py: 1.5, borderRadius: 2.5, textTransform: 'none',
                                        fontWeight: 700, fontSize: '0.9rem',
                                    }}
                                >
                                    Copy to Current Week (Week {currentWeekNumber})
                                </Button>

                                <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center', display: 'block' }}>
                                    This will overwrite the current form data. You can edit before saving.
                                </Typography>
                            </Stack>
                        )}
                    </>
                )}
            </Box>
        </Drawer>
    );
};


// ── Helper: Preview section ──────────────────────────────────────────────────

const PreviewSection = ({ title, icon, items, renderItem }) => {
    if (!items || items.length === 0) return null;

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                {icon}
                <Typography variant="caption" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {title}
                </Typography>
                <Chip label={items.length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
            </Stack>
            <Stack spacing={1}>
                {items.map((item, i) => (
                    <React.Fragment key={i}>{renderItem(item)}</React.Fragment>
                ))}
            </Stack>
        </Paper>
    );
};


export default SubmissionHistory;